/**
 * Admin Usage Analytics Page
 *
 * System-wide usage analytics with charts, trends, and breakdowns.
 *
 * @module @humanizer/studio/components/admin/AdminUsage
 */

import { useState, useEffect, useCallback } from 'react';
import { useApi, type AdminUsageStats } from '../../contexts/ApiContext';
import { createLogger } from '../../utils/debug';

const log = createLogger('AdminUsage');

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

interface UsageBreakdown {
  name: string;
  requests: number;
  tokens: number;
  cost: number;
}

interface DailyUsage {
  date: string;
  requests: number;
  tokens: number;
  users: number;
}

interface ExtendedUsageStats extends AdminUsageStats {
  byModel: UsageBreakdown[];
  byTier: UsageBreakdown[];
  daily: DailyUsage[];
  topUsers: Array<{
    userId: string;
    email: string;
    requests: number;
    tokens: number;
  }>;
}


// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export function AdminUsage() {
  const api = useApi();

  // State
  const [stats, setStats] = useState<ExtendedUsageStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<'7d' | '30d' | '90d'>('30d');
  const [viewMode, setViewMode] = useState<'overview' | 'models' | 'tiers' | 'users'>('overview');

  // ─────────────────────────────────────────────────────────────────────────
  // DATA FETCHING
  // ─────────────────────────────────────────────────────────────────────────

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await api.admin.getUsage();

      // Convert API byModel (Record) to array format if needed
      let byModelArray: UsageBreakdown[] = [];
      if (result.byModel && typeof result.byModel === 'object' && !Array.isArray(result.byModel)) {
        byModelArray = Object.entries(result.byModel as Record<string, { tokens?: number; requests?: number; cost?: number }>)
          .map(([name, data]) => ({
            name,
            requests: data.requests ?? 0,
            tokens: data.tokens ?? 0,
            cost: data.cost ?? 0,
          }));
      }

      // Convert API byTier to UsageBreakdown[] format
      const byTierArray: UsageBreakdown[] = Array.isArray(result.byTier)
        ? result.byTier.map((t) => ({
              name: t.name,
              requests: t.requests ?? 0,
              tokens: t.tokens ?? 0,
              cost: t.cost ?? 0,
            }))
        : [];

      // Convert API daily to DailyUsage[] format
      const dailyArray: DailyUsage[] = Array.isArray(result.daily)
        ? result.daily.map((d) => ({
              date: d.date,
              requests: d.requests ?? 0,
              tokens: d.tokens ?? 0,
              users: d.users ?? 0,
            }))
        : [];

      // Convert API topUsers
      const topUsersArray = Array.isArray(result.topUsers)
        ? result.topUsers.map((u) => ({
              userId: u.userId,
              email: u.email ?? u.userId,
              requests: u.requests ?? 0,
              tokens: u.tokens ?? 0,
            }))
        : [];

      setStats({
        totalTokens: result.totalTokens ?? 0,
        totalRequests: result.totalRequests ?? 0,
        totalCostCents: result.totalCostCents ?? 0,
        userCount: result.userCount ?? 0,
        byModel: byModelArray,
        byTier: byTierArray,
        daily: dailyArray,
        topUsers: topUsersArray,
      });
    } catch (err) {
      log.warn('Usage endpoint not available:', err);
      setError('Usage analytics service unavailable.');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats, period]);

  // ─────────────────────────────────────────────────────────────────────────
  // HELPERS
  // ─────────────────────────────────────────────────────────────────────────

  const formatNumber = (n: number) => {
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n.toString();
  };

  const formatCost = (cents: number) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const getBarWidth = (value: number, max: number) => {
    return `${Math.max((value / max) * 100, 2)}%`;
  };

  const getDaysForPeriod = () => {
    switch (period) {
      case '7d':
        return 7;
      case '30d':
        return 30;
      case '90d':
        return 90;
      default:
        return 30;
    }
  };

  const filteredDaily = stats?.daily.slice(-getDaysForPeriod()) || [];
  const maxDailyRequests = Math.max(...filteredDaily.map((d) => d.requests), 1);
  const maxDailyTokens = Math.max(...filteredDaily.map((d) => d.tokens), 1);

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="admin-usage">
        <div className="admin-loading">
          <span className="admin-loading__spinner" />
        </div>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="admin-usage">
        <div className="admin-alert admin-alert--error">
          <span className="admin-alert__icon">!</span>
          <div className="admin-alert__content">
            <p className="admin-alert__message">{error || 'Failed to load usage data'}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-usage">
      {/* Header */}
      <div className="admin-usage__header">
        <div className="admin-usage__header-left">
          <h1 className="admin-usage__title">Usage Analytics</h1>
          <div className="admin-usage__period-toggle">
            <button
              className={`admin-usage__period-btn ${period === '7d' ? 'admin-usage__period-btn--active' : ''}`}
              onClick={() => setPeriod('7d')}
            >
              7 days
            </button>
            <button
              className={`admin-usage__period-btn ${period === '30d' ? 'admin-usage__period-btn--active' : ''}`}
              onClick={() => setPeriod('30d')}
            >
              30 days
            </button>
            <button
              className={`admin-usage__period-btn ${period === '90d' ? 'admin-usage__period-btn--active' : ''}`}
              onClick={() => setPeriod('90d')}
            >
              90 days
            </button>
          </div>
        </div>
        <div className="admin-usage__view-toggle">
          <button
            className={`admin-usage__view-btn ${viewMode === 'overview' ? 'admin-usage__view-btn--active' : ''}`}
            onClick={() => setViewMode('overview')}
          >
            Overview
          </button>
          <button
            className={`admin-usage__view-btn ${viewMode === 'models' ? 'admin-usage__view-btn--active' : ''}`}
            onClick={() => setViewMode('models')}
          >
            By Model
          </button>
          <button
            className={`admin-usage__view-btn ${viewMode === 'tiers' ? 'admin-usage__view-btn--active' : ''}`}
            onClick={() => setViewMode('tiers')}
          >
            By Tier
          </button>
          <button
            className={`admin-usage__view-btn ${viewMode === 'users' ? 'admin-usage__view-btn--active' : ''}`}
            onClick={() => setViewMode('users')}
          >
            Top Users
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="admin-usage__summary">
        <div className="admin-usage__card">
          <div className="admin-usage__card-label">Total Requests</div>
          <div className="admin-usage__card-value">{formatNumber(stats.totalRequests)}</div>
          <div className="admin-usage__card-trend admin-usage__card-trend--neutral">This period</div>
        </div>
        <div className="admin-usage__card">
          <div className="admin-usage__card-label">Total Tokens</div>
          <div className="admin-usage__card-value">{formatNumber(stats.totalTokens)}</div>
          <div className="admin-usage__card-trend admin-usage__card-trend--neutral">This period</div>
        </div>
        <div className="admin-usage__card">
          <div className="admin-usage__card-label">Provider Cost</div>
          <div className="admin-usage__card-value">{formatCost(stats.totalCostCents)}</div>
          <div className="admin-usage__card-trend admin-usage__card-trend--neutral">This period</div>
        </div>
        <div className="admin-usage__card">
          <div className="admin-usage__card-label">Active Users</div>
          <div className="admin-usage__card-value">{stats.userCount}</div>
          <div className="admin-usage__card-trend admin-usage__card-trend--neutral">Total registered</div>
        </div>
      </div>

      {/* Main Content */}
      <div className="admin-usage__content">
        {viewMode === 'overview' && (
          <>
            {filteredDaily.length === 0 ? (
              <div className="admin-empty">
                <span className="admin-empty__icon">D</span>
                <h3 className="admin-empty__title">No Daily Data</h3>
                <p className="admin-empty__description">
                  Daily usage tracking will populate as requests are processed.
                </p>
              </div>
            ) : (
              <>
                {/* Daily Trend Chart */}
                <div className="admin-usage__section">
                  <h2 className="admin-usage__section-title">Daily Requests</h2>
                  <div className="admin-usage__chart">
                    <div className="admin-usage__chart-bars">
                      {filteredDaily.map((day) => (
                        <div key={day.date} className="admin-usage__chart-bar-container">
                          <div
                            className="admin-usage__chart-bar"
                            style={{ height: getBarWidth(day.requests, maxDailyRequests) }}
                            title={`${day.date}: ${day.requests.toLocaleString()} requests`}
                          />
                        </div>
                      ))}
                    </div>
                    <div className="admin-usage__chart-labels">
                      <span>{filteredDaily[0]?.date}</span>
                      <span>{filteredDaily[filteredDaily.length - 1]?.date}</span>
                    </div>
                  </div>
                </div>

                {/* Token Usage Chart */}
                <div className="admin-usage__section">
                  <h2 className="admin-usage__section-title">Daily Tokens</h2>
                  <div className="admin-usage__chart">
                    <div className="admin-usage__chart-bars admin-usage__chart-bars--tokens">
                      {filteredDaily.map((day) => (
                        <div key={day.date} className="admin-usage__chart-bar-container">
                          <div
                            className="admin-usage__chart-bar admin-usage__chart-bar--tokens"
                            style={{ height: getBarWidth(day.tokens, maxDailyTokens) }}
                            title={`${day.date}: ${day.tokens.toLocaleString()} tokens`}
                          />
                        </div>
                      ))}
                    </div>
                    <div className="admin-usage__chart-labels">
                      <span>{filteredDaily[0]?.date}</span>
                      <span>{filteredDaily[filteredDaily.length - 1]?.date}</span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {viewMode === 'models' && (
          <div className="admin-usage__section">
            <h2 className="admin-usage__section-title">Usage by Model</h2>
            {stats.byModel.length === 0 ? (
              <div className="admin-empty">
                <span className="admin-empty__icon">M</span>
                <h3 className="admin-empty__title">No Model Usage Data</h3>
                <p className="admin-empty__description">
                  Model usage data will appear as requests are processed.
                </p>
              </div>
            ) : (
              <div className="admin-usage__breakdown">
                {stats.byModel.map((item) => {
                  const maxRequests = Math.max(...stats.byModel.map((m) => m.requests));
                  return (
                    <div key={item.name} className="admin-usage__breakdown-item">
                      <div className="admin-usage__breakdown-header">
                        <span className="admin-usage__breakdown-name">{item.name}</span>
                        <span className="admin-usage__breakdown-value">
                          {formatNumber(item.requests)} requests
                        </span>
                      </div>
                      <div className="admin-usage__breakdown-bar">
                        <div
                          className="admin-usage__breakdown-fill"
                          style={{ width: getBarWidth(item.requests, maxRequests) }}
                        />
                      </div>
                      <div className="admin-usage__breakdown-meta">
                        <span>{formatNumber(item.tokens)} tokens</span>
                        <span>{formatCost(item.cost)} cost</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {viewMode === 'tiers' && (
          <div className="admin-usage__section">
            <h2 className="admin-usage__section-title">Usage by Tier</h2>
            {stats.byTier.length === 0 ? (
              <div className="admin-empty">
                <span className="admin-empty__icon">T</span>
                <h3 className="admin-empty__title">Tier Breakdown Unavailable</h3>
                <p className="admin-empty__description">
                  Requires usage tracking service with tier aggregation.
                </p>
              </div>
            ) : (
              <div className="admin-usage__breakdown">
                {stats.byTier.map((item) => {
                  const maxRequests = Math.max(...stats.byTier.map((t) => t.requests));
                  return (
                    <div key={item.name} className="admin-usage__breakdown-item">
                      <div className="admin-usage__breakdown-header">
                        <span className="admin-usage__breakdown-name">{item.name}</span>
                        <span className="admin-usage__breakdown-value">
                          {formatNumber(item.requests)} requests
                        </span>
                      </div>
                      <div className="admin-usage__breakdown-bar">
                        <div
                          className="admin-usage__breakdown-fill admin-usage__breakdown-fill--tier"
                          style={{ width: getBarWidth(item.requests, maxRequests) }}
                        />
                      </div>
                      <div className="admin-usage__breakdown-meta">
                        <span>{formatNumber(item.tokens)} tokens</span>
                        <span>{formatCost(item.cost)} cost</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {viewMode === 'users' && (
          <div className="admin-usage__section">
            <h2 className="admin-usage__section-title">Top Users</h2>
            {stats.topUsers.length === 0 ? (
              <div className="admin-empty">
                <span className="admin-empty__icon">U</span>
                <h3 className="admin-empty__title">User Breakdown Unavailable</h3>
                <p className="admin-empty__description">
                  Requires per-user usage tracking service.
                </p>
              </div>
            ) : (
              <div className="admin-usage__table">
                <div className="admin-usage__table-header">
                  <span className="admin-usage__table-cell admin-usage__table-cell--user">User</span>
                  <span className="admin-usage__table-cell admin-usage__table-cell--num">Requests</span>
                  <span className="admin-usage__table-cell admin-usage__table-cell--num">Tokens</span>
                </div>
                {stats.topUsers.map((user, index) => (
                  <div key={user.userId} className="admin-usage__table-row">
                    <span className="admin-usage__table-cell admin-usage__table-cell--user">
                      <span className="admin-usage__rank">{index + 1}</span>
                      <span className="admin-usage__user-email">{user.email}</span>
                    </span>
                    <span className="admin-usage__table-cell admin-usage__table-cell--num">
                      {user.requests.toLocaleString()}
                    </span>
                    <span className="admin-usage__table-cell admin-usage__table-cell--num">
                      {formatNumber(user.tokens)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
